import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilState, useRecoilValue } from 'recoil';

import { updatePractice } from 'api/Practice';

import { useCurrentPractice } from 'lib/hooks/queries/practice/useCurrentPractice';
import { usePracticeLocations } from 'lib/hooks/queries/practice/usePracticeLocations';

import Button from 'components/shared/Button/Button';
import Divider from 'components/shared/Divider/Divider';
import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import { currentPractice } from 'components/practice/practiceState';
import { withErrorBoundary } from 'components/shared/Error/Boundary';

import formInitialValues from './lib/formInitialValues';
import PracticeBranding from './components/PracticeBranding';
import PracticeIdentity from './components/PracticeIdentity';
import PracticeLocation from './components/PracticeLocation';
import PracticeTimezone from './components/PracticeTimezone';
import formValidationSchema from './lib/formValidationSchema';
import OperationalSettings from './components/OperationalSettings';
import PracticeWorkingDays from './components/PracticeWorkingDays';
import ConsentModal from './components/TechnologyConsent/ConsentModal';
import {
  getDuplicateLocationLabel,
  getUpdateFields,
  handleLocationsSubmit,
  hasAddressChanged,
  hasLocationsChanged,
  hasPracticeFieldsChanged
} from './lib/helpers';

const ReinitializeForm = ({ resetForm, locations }) => {
  const practice = useRecoilValue(currentPractice);
  useEffect(() => {
    resetForm({ values: formInitialValues(practice, locations) });
  }, [practice, locations]);
  return null;
};

const FormTracker = ({ dirty, setHasUnsavedChanges }) => {
  useEffect(() => {
    setHasUnsavedChanges(dirty);
  }, [dirty, setHasUnsavedChanges]);

  return null;
};

const PracticeDetails = () => {
  const addressRef = useRef();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [practice, setPractice] = useRecoilState(currentPractice);
  const [_hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showTechnologyModal, setShowTechnologyModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const pendingNavigation = useRef(null);

  const { data, isLoading } = useCurrentPractice();
  const { data: locationsData } = usePracticeLocations();
  const locations = locationsData?.locations;
  const formikRef = useRef();

  useEffect(() => {
    setPractice((prev) => ({
      ...prev,
      ...(data?.practice || {})
    }));
  }, [data]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const duplicateLabel = getDuplicateLocationLabel(values);
    if (duplicateLabel) {
      showAlert({
        title: 'Duplicate location label',
        message: `"${duplicateLabel}" is already used by another location. Location labels must be unique.`,
        color: 'danger'
      });
      setSubmitting(false);
      return;
    }

    const initialValues = formInitialValues(practice, locations);
    const addressChanged = hasAddressChanged(values?.address, initialValues?.address);
    const practiceChanged = hasPracticeFieldsChanged(values, initialValues);
    const locationsChanged = hasLocationsChanged(values, initialValues);

    if (!addressChanged && !practiceChanged && !locationsChanged) {
      setSubmitting(false);
      return;
    }

    let updateSuccessful = true;

    // Only update address if it has changed
    if (addressChanged) await addressRef.current?.updateAddress();

    // Only update practice if fields have changed
    if (practiceChanged) {
      const updateFields = getUpdateFields(values, initialValues);

      const res = await updatePractice(navigate, {
        practice_id: practice.id,
        fields: updateFields
      });

      if (res.code !== 0) {
        updateSuccessful = false;
      }
    }

    if (locationsChanged) {
      const locationsSuccessful = await handleLocationsSubmit(values, initialValues, { navigate });
      if (!locationsSuccessful) updateSuccessful = false;
      await queryClient.invalidateQueries(['practiceLocations']);
    }

    if (updateSuccessful) {
      await queryClient.invalidateQueries(['currentPractice']);

      setHasUnsavedChanges(false);

      showAlert({
        title: 'Practice details updated',
        color: 'success'
      });
    }

    setSubmitting(false);
  };

  if (isLoading && !practice)
    return <Skeleton count={4} height={40} containerClassName="w-full mb-10" />;

  return (
    <div className="h-full w-full">
      <Formik
        key={isLoading ? 'loading' : 'loaded'}
        innerRef={formikRef}
        initialValues={formInitialValues(practice, locations)}
        validationSchema={formValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}>
        {({ dirty, resetForm, isSubmitting, setFieldValue }) => (
          <Form className="flex h-full w-full flex-col">
            <ReinitializeForm values={practice} resetForm={resetForm} locations={locations} />
            <FormTracker dirty={dirty} setHasUnsavedChanges={setHasUnsavedChanges} />
            <div className="flex flex-1 flex-col overflow-y-auto pb-8">
              <div className="flex w-[770px] flex-col gap-6 self-center p-4">
                <PracticeIdentity />
                <Divider />
                <PracticeBranding />
                <Divider />
                <PracticeLocation ref={addressRef} />
                <Divider />
                <PracticeTimezone />
                <Divider />
                <PracticeWorkingDays />
                <Divider />
                <OperationalSettings setShowTechnologyModal={setShowTechnologyModal} />
              </div>
            </div>
            {dirty && (
              <div className="flex items-center justify-end gap-2 border-0 border-t border-solid border-neutral-200 bg-white p-3">
                <Button
                  text="Cancel"
                  variant="tertiary"
                  color="neutral"
                  size="small"
                  onClick={() => setShowUnsavedModal(true)}
                  disabled={isSubmitting}
                />
                <Button
                  text="Update"
                  color="primary"
                  size="small"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            )}
            <ConsentModal
              showTechnologyModal={showTechnologyModal}
              hideTechnologyModal={() => setShowTechnologyModal(false)}
              setPractice={setPractice}
              practice={practice}
              updateSurcharge={() => setFieldValue('surchargeEnabled', true)}
            />
            <Confirm
              type="unsaved-changes"
              isOpen={showUnsavedModal}
              handleClose={() => setShowUnsavedModal(false)}
              title="Discard unsaved changes?"
              message="Your unsaved changes will be discarded."
              footerActions={{
                keepEditing: true,
                primary: {
                  text: 'Discard',
                  onClick: () => {
                    resetForm();
                    setHasUnsavedChanges(false);
                    setShowUnsavedModal(false);

                    pendingNavigation.current?.retry?.();
                  },
                  color: 'danger'
                },
                secondary: null
              }}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default withErrorBoundary(PracticeDetails);
